import { useState } from 'react'

import {
  Box,
  Button,
  SearchField,
  type SortDirection,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import type JobRole from 'modules/job_catalog/store/models/JobRole'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import AnalyticsTableHeader from './Analytics/TableHeader'
import Row from './Row'
import store, { type RolesSorteableAttribute, SORTABLE_ATTRIBUTES } from './store'

const DEFAULT_COL_SPAN = 8

interface Props {
  loading?: boolean
  loadingAnalytics?: boolean
  canSeeAggregatedInfo?: boolean
  handleRemove: (role: JobRole) => void
}

const BottomOptions = ({ numberOfRows }: { numberOfRows: number }) => (
  <Box width="full" key={numberOfRows}>
    <Text.Normal size="100" weight="medium">
      {i18n.tp('job_catalog.showing.items', { count: numberOfRows })}
    </Text.Normal>
  </Box>
)

const RolesTable = ({ loading, loadingAnalytics, canSeeAggregatedInfo, handleRemove }: Props) => {
  const { navigation } = useNavigation()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [sort, setSort] = useState<{
    direction: SortDirection
    attribute: RolesSorteableAttribute
  }>({
    direction: 'asc',
    attribute: 'name',
  })

  const activeJobRoles = store.jobRoles
  const sortedRoles = store.filteredJobRoles(debouncedSearch, sort.attribute, sort.direction)

  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const isSearchEmpty = activeJobRoles.length > 0 && sortedRoles.length === 0
  const showNoFilteredJobsTable = isSearchEmpty || !canCreateJob

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job', []),
        policyAuthorizations.authorizePolicies('job_catalog.edit_job', activeJobRoles),
      ]),
    []
  )

  const rows = sortedRoles.map((x) => (
    <Row
      key={x.attributes.id}
      role={x}
      isEditable={policyAuthorizations.for('job_catalog.edit_job', x)}
      onRemove={() => handleRemove(x)}
      loadingAnalytics={loadingAnalytics}
      canSeeAggregatedInfo={canSeeAggregatedInfo}
    />
  ))

  const options = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  const handleSortChange = (attribute: string, direction: SortDirection) => {
    const sortAttribute = SORTABLE_ATTRIBUTES.find((x) => x === attribute) ?? SORTABLE_ATTRIBUTES[0]
    setSort({ attribute: sortAttribute, direction })
  }

  const blankState = showNoFilteredJobsTable ? (
    <Table.Blank colSpan={DEFAULT_COL_SPAN} text={i18n.t('job_catalog.no_jobs_found')} />
  ) : (
    <Table.Row>
      <Table.Cell colSpan={DEFAULT_COL_SPAN}>
        <Box alignItems="center" justifyContent="center" padding="s24" textAlign="center">
          <Text.Normal>{i18n.t('job_catalog.import_jobs_blank')}</Text.Normal>
          <Box paddingTop="s32">
            <Button.Router
              type="secondary"
              to={navigation.employees.jobCatalog.list.import.fullPath()}
              iconName="upload"
            >
              {i18n.t('job_catalog.import_jobs_in_bulk')}
            </Button.Router>
          </Box>
        </Box>
      </Table.Cell>
    </Table.Row>
  )

  const content = loading ? (
    <Table.Loading colSpan={DEFAULT_COL_SPAN} />
  ) : (
    <Table.Body>{rows.length ? rows : blankState}</Table.Body>
  )

  return (
    <Table
      options={options}
      fullWidth
      optionsBottom={() => <BottomOptions numberOfRows={rows.length} />}
      bottomSticky
    >
      <Table.Head>
        <Table.Row>
          <Table.SortableHeadCell
            colSpan={2}
            attribute="name"
            onSort={handleSortChange}
            sortAttribute={sort.attribute}
            sortDirection={sort.direction}
          >
            {i18n.t('job_catalog.role')}
          </Table.SortableHeadCell>
          <AnalyticsTableHeader
            onSort={handleSortChange}
            sortAttribute={sort.attribute}
            sortDirection={sort.direction}
            canSeeAggregatedInfo={canSeeAggregatedInfo}
          />
          <Table.HeadCell colSpan={1}></Table.HeadCell>
        </Table.Row>
      </Table.Head>
      {content}
    </Table>
  )
}

export default observer(RolesTable)
